import type { StepState } from './progress';
import { includesMessage, type OutreachFollowConfig } from '@gf/shared/features/outreach';

export const goals = [
  { id: 'outreach', title: 'Start conversations', detail: 'Create a draft outreach campaign for the people you want to reach.', to: '/outreach', start: '/outreach/new', action: 'Create an outreach draft', prompt: 'Help me create my first outreach campaign as a draft. Ask about my audience and offer, explain each step, and let me review before anything runs.' },
  { id: 'engage', title: 'Grow my audience', detail: 'Build an engagement campaign around an audience that fits your business.', to: '/outreach', start: '/outreach/new?type=engage', action: 'Create an engagement draft', prompt: 'Walk me through creating my first engagement campaign as a draft. Help me choose an audience and review pacing before enabling anything.' },
  { id: 'contact', title: 'Organize my leads', detail: 'Save a first contact so you can track conversations and follow-ups.', to: '/crm/contacts', start: '/crm/contacts', action: 'Add my first contact', prompt: 'Help me add my first CRM contact. Ask me for the details, explain where to find it, and help me choose a next step.' },
] as const;
export type GoalId = typeof goals[number]['id'];
export type Journey = { step: number; goal: GoalId; paused: boolean; finished: boolean };
export const freshJourney: Journey = { step: 0, goal: 'outreach', paused: false, finished: false };
export function parseJourney(raw: string | null): Journey {
  try {
    const value = JSON.parse(raw ?? 'null');
    if (!value || typeof value !== 'object') return { ...freshJourney };
    return { step: Number.isInteger(value.step) && value.step >= 0 && value.step <= 4 ? value.step : 0,
      goal: goals.some(g => g.id === value.goal) ? value.goal : 'outreach',
      paused: value.paused === true, finished: value.finished === true };
  } catch { return { ...freshJourney }; }
}
/** Completion requires saved work for the selected account and goal. */
export function firstActionComplete(connected: boolean, goal: GoalId, evidence: Record<GoalId, StepState>) {
  return connected && evidence[goal] === 'done';
}
export function matchesGoal(follow: OutreachFollowConfig, goal: GoalId) {
  return goal === 'outreach' ? includesMessage(follow) : goal === 'engage' && (follow.enabled || follow.sequence?.includes('engage') === true);
}
export function eitherProgress(a: StepState, b: StepState): StepState {
  return a === 'done' || b === 'done' ? 'done' : a === 'error' || b === 'error' ? 'error' : a === 'checking' || b === 'checking' ? 'checking' : 'todo';
}
export function openOnboardingChat(prompt: string) {
  window.dispatchEvent(new CustomEvent('gf-help-assistant', { detail: { panel: 'chat', prompt } }));
}
